import { Fragment, useState, PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Tabs, Tab, Typography } from '@mui/material';
import {
  advertorialChoices,
  applicationChoices,
  contentTypeChoices,
  deletedContentChoices,
  draftContentChoices,
  templateContentChoices,
  flagsChoices,
  photoSalesChoices,
  publicationStatusChoices,
  publishStatusChoices,
  userContributedChoices,
  getSelectSx,
} from 'components/Inputs/Dropdown/dropdownConsts';
import { NumRangeDropdownInput } from 'components/Inputs/Dropdown/NumRangeDropdownInput';
import { SelectDropdownInput } from 'components/Inputs/Dropdown/SelectDropdown/SelectDropdownInput';
import { TextDateRangeDropdownInput } from 'components/Inputs/Dropdown/TextDateRangeDropdownInput';
import { TextDropdownInput } from 'components/Inputs/Dropdown/TextDropdownInput';
import { getPopoverGridInputLabelSx } from 'components/Search/styles';
import { useAdvancedSearchContext } from '../../useAdvancedSearchContext';
import {
  useGetFetchAndFormatAuthors,
  useGetFetchAndFormatPublications,
  useGetFetchAndFormatSections,
  useGetFetchAndFormatWorkflows,
  useGetFetchAndFormatUnlabeledAuthors,
  cacheAuthorChoice,
  getCachedAuthorChoices,
} from '../hooks';
import { bloxValues } from 'themes/constants';

// Keep fields in the outer grid; hide inactive panel without breaking layout
const TabPanel = ({ active, children }: PropsWithChildren<{ active: boolean }>) => (
  <Box
    role="tabpanel"
    sx={{ display: active ? ('contents' as const) : 'none' }}
  >
    {children}
  </Box>
);

export const EditorialPopoverFields = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);

  const { stagedFilters, updateStagedFilterValue, totalCMS } = useAdvancedSearchContext();

  // Editorial Search Specific
  const { fetchAndFormatSections } = useGetFetchAndFormatSections();
  const { fetchAndFormatAuthors } = useGetFetchAndFormatAuthors();
  const { fetchAndFormatUnlabeledAuthors } = useGetFetchAndFormatUnlabeledAuthors();
  const { fetchAndFormatWorkflows } = useGetFetchAndFormatWorkflows();
  const { fetchAndFormatPublications } = useGetFetchAndFormatPublications();

  return (
    <Fragment>
      {/* Full-width tab header */}
      <Box
        sx={{
          gridColumn: '1 / -1',
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
        }}
        className="editorial-popover-tabs"
      >
        <Box sx={{ width: 'fit-content', maxWidth: '100%' }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="standard"
            aria-label="Editorial filter tabs"
            sx={{ p: 0 }}
          >
            <Tab
              label={t('blox.navigation.assets.standard_filters')}
              disableRipple
              sx={{
                px: 0,
                mr: 2,
                textTransform: 'none',
                fontSize: bloxValues.smallFontSizeRem,
              }}
              data-testid="standard-filters-tab"
            />
            <Tab
              label={t('blox.navigation.assets.more_filters')}
              disableRipple
              sx={{
                px: 0,
                textTransform: 'none',
                fontSize: bloxValues.smallFontSizeRem,
              }}
              data-testid="more-filters-tab"
            />
          </Tabs>
        </Box>
      </Box>

      {/* Standard Filters */}
      <TabPanel active={tab === 0}>
        <Typography sx={getPopoverGridInputLabelSx}>
          {t('blox.editorial.asset.type.label')}
        </Typography>
        <SelectDropdownInput
          staticChoices={contentTypeChoices}
          inputLabel={t('blox.editorial.asset.type.label')}
          value={stagedFilters.type}
          updateValue={(event, value) => updateStagedFilterValue('type', value)}
          showFilter={false}
          showFooter={false}
          showChipBox={false}
          popoverMatchesFormControlWidth
          renderValueDisplay="chips"
          formControlVariant="outlined"
          inputPlaceholderText="stringify-options"
          multiple
          selectSx={getSelectSx}
        />

        <Typography sx={getPopoverGridInputLabelSx}>
          {t('blox.editorial.asset.start_time.label')}
        </Typography>
        <TextDateRangeDropdownInput
          inputLabel={t('blox.editorial.asset.start_time.label')}
          value={[
            stagedFilters['start_time[min]'] || '',
            stagedFilters['start_time[max]'] || '',
          ]}
          updateValue={(event, value) => {
            updateStagedFilterValue('start_time[min]', value[0]);
            updateStagedFilterValue('start_time[max]', value[1]);
          }}
          applyChangesOnPopoverClose
          popoverMatchesFormControlWidth
          renderValueDisplay="chips"
          formControlVariant="outlined"
          useCalendarIconInSelect={true}
        />

        <Typography sx={getPopoverGridInputLabelSx}>
          {t('blox.editorial.metadata.group.print_slug.label')}
        </Typography>
        <TextDropdownInput
          inputLabel={t('blox.editorial.metadata.group.print_slug.label')}
          value={stagedFilters.print_slug}
          updateValue={(event, value) => updateStagedFilterValue('print_slug', value)}
          applyChangesOnPopoverClose
          popoverMatchesFormControlWidth
          filterPlaceholderText="Find exact slug..."
          formControlVariant="outlined"
          renderValueDisplay="chips"
          selectSx={getSelectSx}
        />

        <Typography sx={getPopoverGridInputLabelSx}>
          {t('blox.editorial.asset.authors.label_one')}
        </Typography>
        <SelectDropdownInput
          inputLabel={t('blox.editorial.asset.authors.label_one')}
          value={
            Array.isArray(stagedFilters.author)
              ? stagedFilters.author[0] || ''
              : stagedFilters.author || ''
          }
          updateValue={(event, value) => updateStagedFilterValue('author', value)}
          fetchDataFn={fetchAndFormatAuthors}
          fetchUnlabeledItemsFn={fetchAndFormatUnlabeledAuthors}
          getCachedItemsFn={getCachedAuthorChoices}
          onChoiceSelected={cacheAuthorChoice}
          popoverMatchesFormControlWidth
          formControlVariant="outlined"
          inputPlaceholderText="stringify-options"
          renderValueDisplay="chips"
          isDataPrivate={true}
          selectSx={getSelectSx}
        />

        <Typography sx={getPopoverGridInputLabelSx}>
          {t('blox.editorial.asset.sections.label')}
        </Typography>
        <SelectDropdownInput
          inputLabel={t('blox.editorial.asset.sections.label')}
          value={stagedFilters.section}
          updateValue={(event, values) => updateStagedFilterValue('section', values)}
          fetchDataFn={fetchAndFormatSections}
          popoverMatchesFormControlWidth
          renderValueDisplay="chips"
          formControlVariant="outlined"
          inputPlaceholderText="stringify-options"
          multiple
          selectSx={getSelectSx}
        />

        <Typography sx={getPopoverGridInputLabelSx}>
          {t('blox.editorial.metadata.group.workflow.label')}
        </Typography>
        <SelectDropdownInput
          inputLabel={t('blox.editorial.metadata.group.workflow.label')}
          value={stagedFilters.workflow}
          updateValue={(event, value) => updateStagedFilterValue('workflow', value)}
          fetchDataFn={fetchAndFormatWorkflows}
          showFilter
          showFooter={false}
          showChipBox={true}
          popoverMatchesFormControlWidth
          renderValueDisplay="chips"
          formControlVariant="outlined"
          inputPlaceholderText="stringify-options"
          multiple
          selectSx={getSelectSx}
        />

        <Typography sx={getPopoverGridInputLabelSx}>
          {t('blox.editorial.asset.properties.no_publish.label_alt')}
        </Typography>
        <SelectDropdownInput
          staticChoices={publishStatusChoices}
          inputLabel={t('blox.editorial.asset.properties.no_publish.label_alt')}
          value={stagedFilters.publish_status}
          updateValue={(event, value) => updateStagedFilterValue('publish_status', value)}
          showFilter={false}
          showFooter={false}
          showChipBox={false}
          popoverMatchesFormControlWidth
          renderValueDisplay="chips"
          formControlVariant="outlined"
          inputPlaceholderText="stringify-options"
          multiple
          selectSx={getSelectSx}
        />

        <Typography sx={getPopoverGridInputLabelSx}>
          {t('blox.editorial.asset.flags.label')}
        </Typography>
        <SelectDropdownInput
          staticChoices={flagsChoices}
          inputLabel={t('blox.editorial.asset.flags.label')}
          value={stagedFilters.flag}
          updateValue={(event, value) => updateStagedFilterValue('flag', value)}
          popoverMatchesFormControlWidth
          renderValueDisplay="chips"
          formControlVariant="outlined"
          inputPlaceholderText="stringify-options"
          multiple
          selectSx={getSelectSx}
        />
      </TabPanel>

      {/* More Filters */}
      <TabPanel active={tab === 1}>
        <Typography sx={getPopoverGridInputLabelSx}>
          {t('blox.editorial.asset.draft.label')}
        </Typography>
        <SelectDropdownInput
          staticChoices={draftContentChoices}
          inputLabel={t('blox.editorial.asset.draft.label')}
          value={stagedFilters.draft}
          updateValue={(event, value) => updateStagedFilterValue('draft', value)}
          showFilter={false}
          showFooter={false}
          showChipBox={false}
          popoverMatchesFormControlWidth
          formControlVariant="outlined"
          inputPlaceholderText="stringify-options"
          renderValueDisplay="chips"
          selectSx={getSelectSx}
        />

        <Typography sx={getPopoverGridInputLabelSx}>
          {t('blox.general.deleted')}
        </Typography>
        <SelectDropdownInput
          staticChoices={deletedContentChoices}
          inputLabel={t('blox.general.deleted')}
          value={stagedFilters.deleted}
          updateValue={(event, value) => updateStagedFilterValue('deleted', value)}
          showFilter={false}
          showFooter={false}
          showChipBox={false}
          popoverMatchesFormControlWidth
          formControlVariant="outlined"
          inputPlaceholderText="stringify-options"
          renderValueDisplay="chips"
          selectSx={getSelectSx}
        />

        <Typography sx={getPopoverGridInputLabelSx}>
          {t('blox.general.template')}
        </Typography>
        <SelectDropdownInput
          staticChoices={templateContentChoices}
          inputLabel={t('blox.general.template')}
          value={stagedFilters.template}
          updateValue={(event, value) => updateStagedFilterValue('template', value)}
          showFilter={false}
          showFooter={false}
          showChipBox={false}
          popoverMatchesFormControlWidth
          formControlVariant="outlined"
          inputPlaceholderText="stringify-options"
          renderValueDisplay="chips"
          selectSx={getSelectSx}
        />

        <Typography sx={getPopoverGridInputLabelSx}>
          {t('blox.general.value_added_content')}
        </Typography>
        <SelectDropdownInput
          staticChoices={advertorialChoices}
          inputLabel={t('blox.general.value_added_content')}
          value={stagedFilters.advertorial}
          updateValue={(event, value) => updateStagedFilterValue('advertorial', value)}
          showFilter={false}
          showFooter={false}
          showChipBox={false}
          popoverMatchesFormControlWidth
          formControlVariant="outlined"
          inputPlaceholderText="stringify-options"
          renderValueDisplay="chips"
          selectSx={getSelectSx}
        />

        <Typography sx={getPopoverGridInputLabelSx}>
          {t('blox.general.contributed')}
        </Typography>
        <SelectDropdownInput
          staticChoices={userContributedChoices}
          inputLabel={t('blox.general.contributed')}
          value={stagedFilters.contributed}
          updateValue={(event, value) => updateStagedFilterValue('contributed', value)}
          showFilter={false}
          showFooter={false}
          showChipBox={false}
          popoverMatchesFormControlWidth
          formControlVariant="outlined"
          inputPlaceholderText="stringify-options"
          renderValueDisplay="chips"
          selectSx={getSelectSx}
        />

        <Typography sx={getPopoverGridInputLabelSx}>
          {t('blox.editorial.asset.properties.app.label')}
        </Typography>
        <SelectDropdownInput
          staticChoices={applicationChoices}
          inputLabel={t('blox.editorial.asset.properties.app.label')}
          value={stagedFilters.app}
          updateValue={(event, value) => updateStagedFilterValue('app', value)}
          showFilter={false}
          showFooter={false}
          showChipBox={false}
          popoverMatchesFormControlWidth
          renderValueDisplay="chips"
          formControlVariant="outlined"
          inputPlaceholderText="stringify-options"
          multiple
          selectSx={getSelectSx}
        />

        <Typography sx={getPopoverGridInputLabelSx}>
          {t('blox.editorial.asset.properties.photo_sales.label')}
        </Typography>
        <SelectDropdownInput
          staticChoices={photoSalesChoices}
          inputLabel={t('blox.editorial.asset.properties.photo_sales.label')}
          value={stagedFilters.photo_sales}
          updateValue={(event, value) => updateStagedFilterValue('photo_sales', value)}
          showFilter={false}
          showFooter={false}
          showChipBox={false}
          popoverMatchesFormControlWidth
          formControlVariant="outlined"
          inputPlaceholderText="stringify-options"
          renderValueDisplay="chips"
          selectSx={getSelectSx}
        />

        {totalCMS && (
          <Fragment>
            <Typography sx={getPopoverGridInputLabelSx}>
              {t('blox.editorial.metadata.group.print_ready.label')}
            </Typography>
            <SelectDropdownInput
              staticChoices={photoSalesChoices}
              inputLabel={t('blox.editorial.metadata.group.print_ready.label')}
              value={stagedFilters.print_ready}
              updateValue={(event, value) =>
                updateStagedFilterValue('print_ready', value)
              }
              showFilter={false}
              showFooter={false}
              showChipBox={false}
              popoverMatchesFormControlWidth
              formControlVariant="outlined"
              inputPlaceholderText="stringify-options"
              renderValueDisplay="chips"
              selectSx={getSelectSx}
            />

            <Typography sx={getPopoverGridInputLabelSx}>
              {t('blox.editorial.metadata.group.print_depth.label')}
            </Typography>
            <NumRangeDropdownInput
              valueMode="normal"
              inputLabel={t('blox.editorial.metadata.group.print_depth.label')}
              inputPlaceholderText={t(
                'blox.editorial.metadata.group.print_depth.label_alt',
              )}
              value={[
                stagedFilters['print_depth[min]'] || '',
                stagedFilters['print_depth[max]'] || '',
              ]}
              updateValue={(event, value) => {
                updateStagedFilterValue('print_depth[min]', value[0]);
                updateStagedFilterValue('print_depth[max]', value[1]);
              }}
              applyChangesOnPopoverClose={true}
              renderValueDisplay="chips"
              formControlVariant="outlined"
              popoverMatchesFormControlWidth={true}
            />

            <Typography sx={getPopoverGridInputLabelSx}>
              {t('blox.editorial.metadata.group.print_publication.label')}
            </Typography>
            <SelectDropdownInput
              inputLabel={t('blox.editorial.metadata.group.print_publication.label')}
              value={stagedFilters.print_publication}
              fetchDataFn={fetchAndFormatPublications}
              updateValue={(event, value) => {
                if (value === null || value === '') {
                  //Clear out all options that require print_publication to be set
                  updateStagedFilterValue('print_publication_date[min]', '');
                  updateStagedFilterValue('print_publication_date[max]', '');
                  updateStagedFilterValue('print_publication_status', '');
                }
                updateStagedFilterValue('print_publication', value);
              }}
              showFilter={false}
              showFooter={false}
              showChipBox={false}
              popoverMatchesFormControlWidth={true}
              formControlVariant="outlined"
              inputPlaceholderText="stringify-options"
              renderValueDisplay="chips"
              selectSx={getSelectSx}
            />

            <Typography sx={getPopoverGridInputLabelSx}>
              {t('blox.editorial.metadata.group.print_publication_date_range.label')}
            </Typography>
            <TextDateRangeDropdownInput
              inputLabel={
                stagedFilters.print_publication
                  ? t('blox.editorial.metadata.group.print_publication_date_range.label')
                  : t('blox.editorial.metadata.group.print_publication_date_range.help')
              }
              value={[
                stagedFilters['print_publication_date[min]'] || '',
                stagedFilters['print_publication_date[max]'] || '',
              ]}
              updateValue={(event, value) => {
                updateStagedFilterValue('print_publication_date[min]', value[0]);
                updateStagedFilterValue('print_publication_date[max]', value[1]);
              }}
              disabled={Boolean(!stagedFilters.print_publication)}
              applyChangesOnPopoverClose={true}
              popoverMatchesFormControlWidth={true}
              renderValueDisplay="chips"
              formControlVariant="outlined"
              outputFormat="YYYY-MM-DD"
              displayFormat="pretty"
              useCalendarIconInSelect={true}
            />

            <Typography sx={getPopoverGridInputLabelSx}>
              {t('blox.editorial.metadata.group.print_publication_status.label')}
            </Typography>
            <SelectDropdownInput
              staticChoices={publicationStatusChoices}
              inputLabel={
                stagedFilters.print_publication
                  ? t('blox.editorial.metadata.group.print_publication_status.label')
                  : t('blox.editorial.metadata.group.print_publication_status.help')
              }
              value={stagedFilters.print_publication_status}
              updateValue={(event, value) =>
                updateStagedFilterValue('print_publication_status', value)
              }
              disabled={Boolean(!stagedFilters.print_publication)}
              showFilter={false}
              showFooter={false}
              showChipBox={false}
              popoverMatchesFormControlWidth={true}
              formControlVariant="outlined"
              renderValueDisplay="chips"
              selectSx={getSelectSx}
            />
          </Fragment>
        )}
      </TabPanel>
    </Fragment>
  );
};

export default EditorialPopoverFields;
